import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Divider, Alert, CircularProgress, useTheme, alpha,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import supplierService from '../../../services/supplierService';
import type { SupplierType } from '../../../types/venue';

const SUPPLIER_TYPES: SupplierType[] = ['DISTRIBUTOR', 'DIRECT_IMPORT', 'AUCTION', 'MERCHANT', 'OTHER'];

interface FormData {
  name: string;
  shortName: string;
  type: SupplierType | '';
  country: string;
  accountReference: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  notes: string;
}

const INITIAL_FORM: FormData = {
  name: '',
  shortName: '',
  type: '',
  country: '',
  accountReference: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  website: '',
  notes: '',
};

const SupplierForm: React.FC = () => {
  const navigate = useNavigate();
  const { supplierId } = useParams<{ supplierId: string }>();
  const theme = useTheme();
  const isEdit = Boolean(supplierId);

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!isEdit || !supplierId) return;
    setLoading(true);
    supplierService.getSupplier(supplierId)
      .then(supplier => {
        setFormData({
          name: supplier.name,
          shortName: supplier.shortName ?? '',
          type: supplier.type,
          country: supplier.country ?? '',
          accountReference: supplier.accountReference ?? '',
          contactName: supplier.contactName ?? '',
          contactEmail: supplier.contactEmail ?? '',
          contactPhone: supplier.contactPhone ?? '',
          website: supplier.website ?? '',
          notes: supplier.notes ?? '',
        });
      })
      .catch(() => setApiError('Failed to load supplier.'))
      .finally(() => setLoading(false));
  }, [isEdit, supplierId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Supplier name is required.';
    if (!formData.type) newErrors.type = 'Type is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setApiError('');
    try {
      const payload = {
        name: formData.name.trim(),
        shortName: formData.shortName.trim() || undefined,
        type: formData.type as SupplierType,
        country: formData.country.trim() || undefined,
        accountReference: formData.accountReference.trim() || undefined,
        contactName: formData.contactName.trim() || undefined,
        contactEmail: formData.contactEmail.trim() || undefined,
        contactPhone: formData.contactPhone.trim() || undefined,
        website: formData.website.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        active: true,
      };
      if (isEdit && supplierId) {
        await supplierService.updateSupplier(supplierId, payload);
        navigate(`/admin/suppliers/${supplierId}`);
      } else {
        const created = await supplierService.createSupplier(payload as Parameters<typeof supplierService.createSupplier>[0]);
        navigate(`/admin/suppliers/${created._id}`);
      }
    } catch (err) {
      console.error('Failed to save supplier', err);
      setApiError('Failed to save supplier. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/suppliers')} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Suppliers
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Supplier' : 'New Supplier'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* Name + Short Name */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Supplier Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={Boolean(errors.name)}
              helperText={errors.name}
              fullWidth
              required
            />
            <TextField
              label="Short Name"
              name="shortName"
              value={formData.shortName}
              onChange={handleInputChange}
              placeholder="e.g. Domaine X"
              sx={{ minWidth: 180 }}
            />
          </Box>

          {/* Type + Country */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth required error={Boolean(errors.type)}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={e => {
                  setFormData(prev => ({ ...prev, type: e.target.value as SupplierType }));
                  setErrors(prev => ({ ...prev, type: undefined }));
                }}
              >
                {SUPPLIER_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
                ))}
              </Select>
              {errors.type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.type}</Typography>
              )}
            </FormControl>
            <TextField
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              fullWidth
              placeholder="e.g. France"
            />
          </Box>

          {/* Account Reference */}
          <TextField
            label="Account Reference"
            name="accountReference"
            value={formData.accountReference}
            onChange={handleInputChange}
            fullWidth
            placeholder="Supplier's account or customer number"
          />

          {/* Contact */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Contact Name"
              name="contactName"
              value={formData.contactName}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Contact Email"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleInputChange}
              fullWidth
              type="email"
            />
            <TextField
              label="Contact Phone"
              name="contactPhone"
              value={formData.contactPhone}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>

          {/* Website */}
          <TextField
            label="Website"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            fullWidth
            placeholder="https://"
          />

          {/* Notes */}
          <TextField
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
          />

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/admin/suppliers')} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 140 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Create Supplier'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SupplierForm;
